import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSpaces } from '../contexts/SpacesContext';
import useActivePromos, { findPromo, applyPromo } from '../hooks/useActivePromos';

const Feature = ({ icon, title, desc }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600">
      {icon}
    </div>
    <div>
      <h4 className="font-semibold text-slate-800">{title}</h4>
      <p className="text-sm text-slate-500 mt-0.5">{desc}</p>
    </div>
  </div>
);

const SpaceTypeCard = ({ space, promos }) => {
  const firstOpt = space.pricingOptions[0];
  const promo = findPromo(promos || [], space.id, firstOpt.type);
  const promoPrice = promo ? applyPromo(promo, firstOpt.price) : null;
  const unit = firstOpt.type === 'hourly' ? 'hr' : firstOpt.type === 'daily' ? 'day' : 'mo';

  return (
    <div className="card group hover:shadow-md transition-shadow">
      <div className="h-2" style={{ backgroundColor: space.color }} />
      {promo && (
        <div className="bg-brand-500 text-white text-xs font-semibold px-4 py-1.5 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          {promo.name}
        </div>
      )}
      <div className="p-6">
        <div className="w-10 h-10 rounded-xl mb-4 flex items-center justify-center" style={{ backgroundColor: space.bgColor }}>
          {space.type === 'room' ? (
            <svg className="w-5 h-5" style={{ color: space.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          ) : (
            <svg className="w-5 h-5" style={{ color: space.color }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
            </svg>
          )}
        </div>
        <h3 className="font-bold text-lg text-slate-800 mb-1">{space.name}</h3>
        <p className="text-sm text-slate-500 mb-4 leading-relaxed">{space.description}</p>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {space.amenities.slice(0, 3).map((a) => (
            <span key={a} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-slate-600">{a}</span>
          ))}
          {space.amenities.length > 3 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-slate-400">+{space.amenities.length - 3} more</span>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs text-slate-400">From</span>
            <div className="font-bold text-slate-800">
              {promoPrice !== null ? (
                <>
                  <span className="line-through text-slate-400 font-normal text-sm">${firstOpt.price}</span>{' '}
                  <span className="text-brand-600">${promoPrice}</span>
                </>
              ) : (
                <>${firstOpt.price}</>
              )}
              <span className="text-xs font-normal text-slate-400 ml-1">/{unit}</span>
            </div>
          </div>
          <Link to={`/book?space=${space.id}`} className="btn-primary text-sm px-4 py-2">Book Now</Link>
        </div>
      </div>
    </div>
  );
};

const CAROUSEL_IMAGES = [
  { src: '/images/carousel-1.jpg', caption: '' },
  { src: '/images/carousel-2.jpg', caption: '' },
  { src: '/images/carousel-3.jpg', caption: '' },
];

const ImageCarousel = ({ images = CAROUSEL_IMAGES }) => {
  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  const goTo = useCallback((index) => {
    if (transitioning) return;
    setTransitioning(true);
    setTimeout(() => {
      setCurrent(index);
      setTransitioning(false);
    }, 200);
  }, [transitioning]);

  const prev = () => goTo((current - 1 + images.length) % images.length);
  const next = useCallback(() => goTo((current + 1) % images.length), [current, goTo, images.length]);

  useEffect(() => {
    const id = setInterval(next, 5000);
    return () => clearInterval(id);
  }, [next]);

  return (
    <div className="relative w-full overflow-hidden rounded-2xl shadow-xl" style={{ aspectRatio: '16/7' }}>
      {images.map((img, i) => (
        <div
          key={img.src}
          className="absolute inset-0 transition-opacity duration-500"
          style={{ opacity: i === current ? (transitioning ? 0 : 1) : 0 }}
        >
          <img
            src={img.src}
            alt={img.caption || `Slide ${i + 1}`}
            className="w-full h-full object-cover"
          />
          {img.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-8 py-6">
              <p className="text-white text-lg font-semibold">{img.caption}</p>
            </div>
          )}
        </div>
      ))}

      {/* Prev / Next */}
      <button
        onClick={prev}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
        aria-label="Previous"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <button
        onClick={next}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors"
        aria-label="Next"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-white w-5' : 'bg-white/50'}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};


export default function HomePage() {
  const SPACES = useSpaces();
  const activePromos = useActivePromos();
  return (
    <div>
      {/* Hero */}
      <section className="relative text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(/images/hero.jpg)' }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(37,40,71,0.65)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
          <div className="inline-flex items-center gap-2 bg-brand-500/20 border border-brand-500/30 text-brand-300 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            <span className="w-2 h-2 bg-brand-400 rounded-full animate-pulse" />
            Desks available now
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
            Your Place to Work
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            A professional co-working space designed for the Southern Tablelands community. Hot desks, dedicated offices, and meeting rooms — all with fast internet.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/book" className="btn-primary text-base px-8 py-3 shadow-lg shadow-brand-500/30">
              Book a Space
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <Feature
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" /></svg>}
              title="Fast Internet"
              desc="High-speed internet with failover 4G backup — always connected."
            />
            <Feature
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>}
              title="Open 7 Days a Week"
              desc="We're open every day so you can work on your schedule."
            />
            <Feature
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
              title="Community"
              desc="Join a growing network of local professionals and entrepreneurs."
            />
            <Feature
              icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>}
              title="Simple Booking"
              desc="Book online in minutes. Digital contracts, no paperwork."
            />
          </div>
        </div>
      </section>

      {/* Spaces */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800 mb-4">Our Spaces</h2>
            <p className="text-lg text-slate-500 max-w-xl mx-auto">
              Whether you need a flexible desk for a day or a permanent home for your business — we have the right space.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SPACES.map((space) => (
              <SpaceTypeCard key={space.id} space={space} promos={activePromos} />
            ))}
          </div>
        </div>
      </section>

      {/* Photo Carousel */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800 mb-4">The Hub Crookwell</h2>
            <p className="text-lg text-slate-500 max-w-xl mx-auto">
              A professional workspace designed for the Southern Tablelands community.
            </p>
          </div>
          <ImageCarousel />
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16" style={{ backgroundColor: '#252847' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">Ready to get started?</h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="bg-brand-500 hover:bg-brand-400 text-navy-800 font-semibold px-8 py-3 rounded-lg transition-colors text-base inline-block" style={{ color: '#252847' }}>
              Book Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
